import { ChangeEvent, useEffect, useRef, useState } from "react";

enum LabelSize {
  small = 1,
  medium,
  large,
  xlarge,
}

type LabelProps = {
  id?: string;
  size: LabelSize;
  title: string;
  baseColor: string;
  highlightColor: string;
  leftIcon?: string;
  onLeftIconClick?: () => void;
  leftIconId?: string;
  rightIcon?: string;
  onRightIconClick?: () => void;
  rightIconId?: string;
  onDoubleClick?: () => unknown;
  canEdit?: boolean;
  onEditValueChange?: (e: ChangeEvent<HTMLInputElement>) => boolean; //canEdit일시에만 사용
  onEdit?: (value: string) => void; //canEdit일시에 right icon 클릭시 사용
  onBlur?: () => unknown;
  editPlaceHolder?: string;
  isGradient?: boolean;
};

// https://www.figma.com/design/xnr558XUCXb4vkBy6eAif7/%5B%EB%9D%BC%EC%9D%B4%EB%B8%8C%EB%9F%AC%EB%A6%AC%5D-Potoo-Library?node-id=1700-52&t=3lunUvpzgaQBTI5S-4
const Label = (props: LabelProps) => {
  const {
    id,
    size,
    title,
    baseColor,
    highlightColor,
    leftIcon,
    onLeftIconClick,
    leftIconId,
    rightIcon,
    onRightIconClick,
    rightIconId,
    onDoubleClick,
    canEdit,
    onEditValueChange,
    onEdit, //canEdit일시에 right icon 클릭시 사용,
    onBlur,
    editPlaceHolder = "",
    isGradient = false,
  } = props;

  const [value, setValue] = useState(title);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const spanRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (inputRef.current && spanRef.current) {
      // span에 value를 설정하여 너비 계산
      spanRef.current.textContent = value || " ";
      inputRef.current.style.width = `${spanRef.current.offsetWidth}px`;
    }
  }, [inputRef, spanRef, value, canEdit]);

  if (canEdit) {
    return (
      <div
        id={id}
        className={`label ${size === LabelSize.small ? "small" : size === LabelSize.medium ? "medium" : size === LabelSize.large ? "large" : "xlarge"}`}
        style={{
          backgroundColor: baseColor,
          color: highlightColor,
        }}
        onBlur={() => {
          //timeout 안 쓰면 저장 전에 blur처리가 먼저 돼서, 0.1초 후에 onBlur 실행
          setTimeout(() => {
            onBlur && onBlur();
          }, 100);
        }}
      >
        {leftIcon && <i className={`icon ${leftIcon}`} onClick={onLeftIconClick} />}
        <input
          ref={inputRef}
          placeholder={editPlaceHolder}
          autoFocus
          type="text"
          value={value}
          style={{
            background: isGradient ? 'linear-gradient(90deg, #A734F4 0%, #640CE8 87.5%)' : 'none',
            WebkitBackgroundClip: isGradient ? 'text' : 'none',
            WebkitTextFillColor: isGradient ? 'transparent' : 'inherit',
          }}
          onChange={(e) => {
            //onEditValueChange가 있고 true를 return할 때 value를 변경
            const available = onEditValueChange && onEditValueChange(e);
            if (available) setValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onEdit && onEdit(value);
            }
          }}
        />

        {rightIcon && (
          <i
            id="label-right-icon"
            className={`icon ${rightIcon}`}
            onClick={() => {
              onEdit && onEdit(value);
            }}
          />
        )}

        {/* 텍스트 길이 측정을 위한 hidden span */}
        <span
          ref={spanRef}
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "pre",
            fontSize: "inherit",
            fontFamily: "inherit",
          }}
        />
      </div>
    );
  }

  return (
    <div
      id={id}
      className={`label ${size === LabelSize.small ? "small" : size === LabelSize.medium ? "medium" : size === LabelSize.large ? "large" : "xlarge"}`}
      style={{
        backgroundColor: baseColor,
        color: highlightColor,
      }}
      onDoubleClick={() => {
        onDoubleClick && onDoubleClick();
        setValue(title);
      }}
    >
      {leftIcon && <i className={`icon ${leftIcon}`} onClick={onLeftIconClick} id={leftIconId} />}
      <span style={{
        background: isGradient ? 'linear-gradient(90deg, #A734F4 0%, #640CE8 87.5%)' : 'none',
        WebkitBackgroundClip: isGradient ? 'text' : 'none',
        WebkitTextFillColor: isGradient ? 'transparent' : 'inherit',
      }}>{title}</span>
      {rightIcon && (
        <i className={`icon ${rightIcon}`} onClick={onRightIconClick} id={rightIconId} />
      )}
    </div>
  );
};

export { Label, LabelSize };
